import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../lib/utils';
import { validatePassword } from '../../utils/validations';

interface PasswordInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  placeholder?: string;
  showStrength?: boolean;
  required?: boolean;
  className?: string;
}

export function PasswordInput({
  label,
  name,
  value,
  onChange,
  icon,
  placeholder,
  showStrength = false,
  required = true,
  className
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  const passwordErrors = showStrength ? validatePassword(value) : [];
  const strength = showStrength ? Math.max(0, 4 - passwordErrors.length) : 0;
  
  const getStrengthColor = () => {
    if (strength === 0) return 'bg-gray-200';
    if (strength === 1) return 'bg-red-500';
    if (strength === 2) return 'bg-orange-500';
    if (strength === 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  const getStrengthText = () => {
    if (strength === 0) return 'Très faible';
    if (strength === 1) return 'Faible';
    if (strength === 2) return 'Moyen';
    if (strength === 3) return 'Bon';
    return 'Excellent';
  };

  return (
    <div className={cn('relative', className)}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
            {icon}
          </div>
        )}
        
        <input
          type={showPassword ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          required={required}
          className={cn(
            'block w-full px-4 py-3 rounded-xl border transition-all duration-200',
            'bg-white/50 backdrop-blur-sm pr-10',
            icon && 'pl-10',
            'border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20',
            'placeholder-gray-400 text-gray-900'
          )}
        />
        
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
      
      {showStrength && value && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className={cn('h-full rounded-full transition-all duration-300', getStrengthColor())}
                style={{ width: `${(strength / 4) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-600 min-w-[60px]">
              {getStrengthText()}
            </span>
          </div>
          
          {(isFocused || passwordErrors.length > 0) && (
            <div className="space-y-1">
              <p className="text-xs text-gray-600 font-medium">Le mot de passe doit contenir :</p>
              <ul className="text-xs space-y-1">
                <li className={cn(
                  'flex items-center gap-2',
                  value.length >= 8 ? 'text-green-600' : 'text-gray-500'
                )}>
                  <span className={cn(
                    'w-1.5 h-1.5 rounded-full',
                    value.length >= 8 ? 'bg-green-500' : 'bg-gray-300'
                  )} />
                  Au moins 8 caractères
                </li>
                <li className={cn(
                  'flex items-center gap-2',
                  /[a-z]/.test(value) ? 'text-green-600' : 'text-gray-500'
                )}>
                  <span className={cn(
                    'w-1.5 h-1.5 rounded-full',
                    /[a-z]/.test(value) ? 'bg-green-500' : 'bg-gray-300'
                  )} />
                  Une minuscule
                </li>
                <li className={cn(
                  'flex items-center gap-2',
                  /[A-Z]/.test(value) ? 'text-green-600' : 'text-gray-500'
                )}>
                  <span className={cn(
                    'w-1.5 h-1.5 rounded-full',
                    /[A-Z]/.test(value) ? 'bg-green-500' : 'bg-gray-300'
                  )} />
                  Une majuscule
                </li>
                <li className={cn(
                  'flex items-center gap-2',
                  /[0-9]/.test(value) ? 'text-green-600' : 'text-gray-500'
                )}>
                  <span className={cn(
                    'w-1.5 h-1.5 rounded-full',
                    /[0-9]/.test(value) ? 'bg-green-500' : 'bg-gray-300'
                  )} />
                  Un chiffre
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}